import { dashboardSectionIndex } from '../config/dashboardSectionIndex';
import type { NavigationMenuSection } from '../config/navigationMenu';

export interface MenuSearchResult {
  type: 'dashboard' | 'section';
  dashboardId: string;
  sectionId?: string;
  icon: string;
  title: string;
  breadcrumb: string;
  score: number;
}

const BREADCRUMB_SEP = ' › ';
const MAX_RESULTS = 10;

export function normalizeSearchText(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[\u200c\u200f]/g, '')
    .replace(/ي/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/\s+/g, ' ');
}

function scoreMatch(normalizedQuery: string, ...fields: (string | undefined)[]): number {
  let best = 0;
  for (const field of fields) {
    if (!field) continue;
    const normalizedField = normalizeSearchText(field);
    if (!normalizedField.includes(normalizedQuery)) continue;
    if (normalizedField.startsWith(normalizedQuery)) {
      best = Math.max(best, 100);
    } else {
      best = Math.max(best, 80);
    }
  }
  return best;
}

function scoreKeywordMatch(normalizedQuery: string, keywords?: string[]): number {
  if (!keywords?.length) return 0;
  const matched = keywords.some((kw) => normalizeSearchText(kw).includes(normalizedQuery));
  return matched ? 60 : 0;
}

function buildBreadcrumb(menuSectionTitle: string, dashboardName: string, sectionTitle?: string): string {
  const parts = [menuSectionTitle, dashboardName];
  if (sectionTitle) parts.push(sectionTitle);
  return parts.join(BREADCRUMB_SEP);
}

export function searchMenu(
  query: string,
  visibleSections: NavigationMenuSection[],
  visibleDashboardIds: Set<string>,
): MenuSearchResult[] {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return [];

  const results: MenuSearchResult[] = [];

  visibleSections.forEach((section) => {
    section.items.forEach((item) => {
      const titleScore = scoreMatch(normalizedQuery, item.name);
      const keywordScore = scoreKeywordMatch(normalizedQuery, item.keywords);
      const sectionScore = scoreMatch(normalizedQuery, section.title);
      const score = Math.max(titleScore, keywordScore, sectionScore > 0 ? 40 : 0);

      if (score > 0) {
        results.push({
          type: 'dashboard',
          dashboardId: item.id,
          icon: item.icon,
          title: item.name,
          breadcrumb: buildBreadcrumb(section.title, item.name),
          score: score === 40 && titleScore === 0 && keywordScore === 0 ? 40 : Math.max(titleScore, keywordScore, 40),
        });
      }
    });
  });

  dashboardSectionIndex.forEach((entry) => {
    if (!visibleDashboardIds.has(entry.dashboardId)) return;

    let parentSection: NavigationMenuSection | undefined;
    let parentItem: NavigationMenuSection['items'][number] | undefined;

    for (const section of visibleSections) {
      const item = section.items.find((i) => i.id === entry.dashboardId);
      if (item) {
        parentSection = section;
        parentItem = item;
        break;
      }
    }

    if (!parentSection || !parentItem) return;

    const titleScore = scoreMatch(normalizedQuery, entry.title);
    const keywordScore = scoreKeywordMatch(normalizedQuery, entry.keywords);
    const dashboardScore = scoreMatch(normalizedQuery, parentItem.name) > 0 ? 40 : 0;
    const score = Math.max(titleScore, keywordScore, dashboardScore);

    if (score > 0) {
      results.push({
        type: 'section',
        dashboardId: entry.dashboardId,
        sectionId: entry.sectionId,
        icon: parentItem.icon,
        title: entry.title,
        breadcrumb: buildBreadcrumb(parentSection.title, parentItem.name, entry.title),
        score,
      });
    }
  });

  return results
    .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title, 'fa'))
    .slice(0, MAX_RESULTS);
}
